import type { Invoice } from '@/api/invoices';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useInvoiceMutations } from '@/hooks/use-invoices';
import { useTranslation } from '@kgc/i18n';
import { toast } from '@kgc/ui';
import { useState, type FormEvent } from 'react';

interface StornoModalProps {
  invoice: Invoice;
  onClose: () => void;
  onStornoed: () => void;
}

/**
 * Sztornó modal (37-kör FE-3, money-write) — a PV-ERTEKESITES-002 kliens-spec happy-path FE-je.
 *
 * A `POST /invoices/:id/storno`-t hívja a `useInvoiceMutations().createStorno`-n át (res-direkt:
 * a `createStornoInvoice` FE-fn wrappeli `{data}`-ba, a hook `response.data`-t ad → IInvoice).
 * Mezők: `reason` (kötelező) + termék-visszahozatal-toggle → ellenőrzés-checkbox (gap-3, #722).
 * A BE 400-at dob, ha `isProductReturn && !inspectionConfirmed` (»köztes-ellenőrzés után«) — a
 * modal a checkbox-szal ELŐZI meg (submit-disabled), és ha mégis 400 jön, a message-t toast-eli.
 * A false-affordance-fix: a régi `?storno=` form-navigáció helyett valódi modal (money-write GO).
 */
export function StornoModal({ invoice, onClose, onStornoed }: StornoModalProps) {
  const { t } = useTranslation();
  const { createStorno, isLoading } = useInvoiceMutations();
  const [reason, setReason] = useState('');
  const [isProductReturn, setIsProductReturn] = useState(false);
  const [inspectionConfirmed, setInspectionConfirmed] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      return;
    }
    try {
      await createStorno(invoice.id, {
        reason: reason.trim(),
        isProductReturn,
        inspectionConfirmed,
      });
      toast.success(t('invoice.storno.success'));
      onStornoed();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('invoice.storno.error');
      toast.error(message);
    }
  };

  // gap-3 UI-guard: termék-visszahozatalnál ellenőrzés-confirm nélkül a submit tiltott
  // (a BE-400 backstop mellett — defense-in-depth, mint a #662/#664 worksheet-gate).
  const submitDisabled = !reason.trim() || isLoading || (isProductReturn && !inspectionConfirmed);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('invoice.storno.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={e => void handleSubmit(e)}
            className="space-y-4"
            data-testid="storno-form"
          >
            <p className="text-sm text-muted-foreground">
              {t('invoice.storno.subtitle')}: {invoice.invoiceNumber}
            </p>
            <div>
              <label className="mb-2 block text-sm font-medium" htmlFor="storno-reason">
                {t('invoice.storno.reasonLabel')}
              </label>
              <textarea
                id="storno-reason"
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value)}
                required
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isProductReturn}
                onChange={e => setIsProductReturn(e.target.checked)}
                data-testid="storno-product-return"
              />
              {t('invoice.storno.isProductReturnLabel')}
            </label>
            {isProductReturn && (
              <label className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                <input
                  type="checkbox"
                  checked={inspectionConfirmed}
                  onChange={e => setInspectionConfirmed(e.target.checked)}
                  data-testid="storno-inspection"
                />
                {t('invoice.storno.inspectionLabel')}
              </label>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={submitDisabled}
                data-testid="storno-submit"
              >
                {t('invoice.storno.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
